import React, { useCallback, useMemo, useRef } from 'react';
import { Input, Row, Col, FormGroup, Button, InputGroup, InputGroupAddon } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'react-datepicker';
import {
    TermForm,
    PurchasePriceSummary,
    DeferredFixedPaymentRow,
    DEFERRED_FIXED_PAYMENT_COLUMNS,
    DEFERRED_FIXED_PAYMENT_COLUMN_LABELS,
    DEFERRED_FIXED_PAYMENT_PERCENTAGE_COLUMNS,
    DEFERRED_FIXED_PAYMENT_CURRENCY_COLUMNS,
    DeferredVariablePaymentRow,
    DEFERRED_VARIABLE_PAYMENT_COLUMNS,
    DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS,
    DEFERRED_VARIABLE_PAYMENT_CURRENCY_COLUMNS,
    DeferredVariablePaymentType,
    JvEquityRow,
    JV_EQUITY_COLUMNS,
    JV_EQUITY_COLUMN_LABELS,
    JV_EQUITY_CURRENCY_COLUMNS,
    CorporateEquityRow,
    CORPORATE_EQUITY_COLUMNS,
    CORPORATE_EQUITY_COLUMN_LABELS,
    CORPORATE_EQUITY_CURRENCY_COLUMNS,
} from '../../../../../../slices/marketplace/loi-form/term-form';
import { LegalIssueItem } from '../../../../../../slices/marketplace/loi-form/legal-issue-item';
import { TermsFormPriceSummaryBlock } from './TermsFormPriceSummaryBlock';
import { TermsFormDetailBlock } from './TermsFormDetailBlock';
import { TermsFormLeaseTermsBlock } from './TermsFormLeaseTermsBlock';
import { TermsFormGeneralDeferredPaymentsBlock } from './TermsFormGeneralDeferredPaymentsBlock';
import { TermsFormNonCompetitionBlock } from './TermsFormNonCompetitionBlock';
import { TermsFormPropertyPurchasedBlock } from './TermsFormPropertyPurchasedBlock';
import './TermsFormDisplay.scss';

export interface TermsFormDisplayProps {
    termForm: TermForm;
    legalIssues?: LegalIssueItem[];
    additionalCovenants?: LegalIssueItem[];
    readOnly?: boolean;
    onChange?: (form: TermForm) => void;
    errors?: Set<string>;
}


function totalDfp(rows: DeferredFixedPaymentRow[]): number {
    return rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
}
function totalDvp(rows: DeferredVariablePaymentRow[]): number {
    return rows.reduce((s, r) => s + (Number(r.valueAtClosing) || 0), 0);
}
function summaryTotal(summary: PurchasePriceSummary | null | undefined): number {
    if (!summary) return 0;
    const cash = Number(summary.cashAtCloser?.cashAtClose) || 0;
    const wch = Number(summary.cashAtCloser?.workingCapitalHoldback?.value) || 0;
    const netCashAtCloser = Number(summary.cashAtCloser?.netCashAtCloser) || 0;
    const dfp = totalDfp(summary.deferredFixedPayments?.rows ?? []);
    const dvp = totalDvp(summary.deferredVariablePayments?.rows ?? []);
    const jv = Number(summary.jvEquity?.row?.valueAtClosing) || 0;
    const corp = Number(summary.corporateEquity?.row?.valueAtClosing) || 0;
    const other = Number(summary.other) || 0;
    return cash + wch + netCashAtCloser + dfp + dvp + jv + corp + other;
}

function rowToKeyValue(row: DeferredFixedPaymentRow): Record<string, string | null> {
    return {
        [DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.paymentTiming]: row.paymentTiming ?? null,
        [DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.amount]:
            row.amount != null ? String(row.amount) : null,
        [DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.annualizedInterestAccrualRate]:
            row.annualizedInterestAccrualRate ?? null,
        [DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contingent]: row.contingent ?? null,
        [DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contingencyDependentVariable]:
            row.contingencyDependentVariableValue ?? null,
        [DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contDvMinimumValue]:
            row.contDvMinimumValue != null ? String(row.contDvMinimumValue) : null,
        [DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.employmentContingency]:
            row.employmentContingency ?? null,
        [DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.tailPeriodForEmployment]:
            row.tailPeriodForEmployment ?? null,
    };
}
function rowFromKeyValue(kv: Record<string, string | null>): DeferredFixedPaymentRow {
    return {
        paymentTiming: kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.paymentTiming] ?? null,
        amount:
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.amount] != null &&
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.amount] !== ''
                ? Number(kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.amount])
                : null,
        annualizedInterestAccrualRate:
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.annualizedInterestAccrualRate] ?? null,
        contingent: kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contingent] ?? null,
        contingencyDependentVariableValue:
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contingencyDependentVariable] ?? null,
        contDvMinimumValue:
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contDvMinimumValue] != null &&
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contDvMinimumValue] !== ''
                ? Number(kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contDvMinimumValue])
                : null,
        employmentContingency:
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.employmentContingency] ?? null,
        tailPeriodForEmployment:
            kv[DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.tailPeriodForEmployment] ?? null,
    };
}
function dvpRowToKeyValue(row: DeferredVariablePaymentRow): Record<string, string | null> {
    return {
        [DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.paymentTiming]: row.paymentTiming ?? null,
        [DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.type]:
            row.type != null ? String(row.type) : null,
        [DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.valueAtClosing]:
            row.valueAtClosing != null ? String(row.valueAtClosing) : null,
        [DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calculationDependentVariable]:
            row.calculationDependentVariable ?? null,
        [DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calcDvBaselineValue]:
            row.calcDvBaselineValue != null ? String(row.calcDvBaselineValue) : null,
        [DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calculationMultiple]:
            row.calculationMultiple ?? null,
        [DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.maximumPayment]:
            row.maximumPayment ?? null,
        [DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.employmentContingency]:
            row.employmentContingency ?? null,
        [DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.tailPeriodForEmploymentContingency]:
            row.tailPeriodForEmploymentContingency ?? null,
        [DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.penaltyMultiple]:
            row.penaltyMultiple ?? null,
        [DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.distributionPeriodicity]:
            row.distributionPeriodicity ?? null,
    };
}
function dvpRowFromKeyValue(kv: Record<string, string | null>): DeferredVariablePaymentRow {
    const typeVal = kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.type];
    let type: DeferredVariablePaymentType | null = null;
    if (typeVal === DeferredVariablePaymentType.Earnout || typeVal === DeferredVariablePaymentType.Synthetic) {
        type = typeVal;
    } else if (typeVal?.toLowerCase().includes('synthetic')) {
        type = DeferredVariablePaymentType.Synthetic;
    } else if (typeVal?.toLowerCase() === 'earnout') {
        type = DeferredVariablePaymentType.Earnout;
    }
    return {
        paymentTiming: kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.paymentTiming] ?? null,
        type,
        valueAtClosing:
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.valueAtClosing] != null &&
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.valueAtClosing] !== ''
                ? Number(kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.valueAtClosing])
                : null,
        calculationDependentVariable:
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calculationDependentVariable] ?? null,
        calcDvBaselineValue:
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calcDvBaselineValue] != null &&
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calcDvBaselineValue] !== ''
                ? Number(kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calcDvBaselineValue])
                : null,
        calculationMultiple:
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calculationMultiple] ?? null,
        maximumPayment: kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.maximumPayment] ?? null,
        employmentContingency:
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.employmentContingency] ?? null,
        tailPeriodForEmploymentContingency:
            kv[
                DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.tailPeriodForEmploymentContingency
            ] ?? null,
        penaltyMultiple: kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.penaltyMultiple] ?? null,
        distributionPeriodicity:
            kv[DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.distributionPeriodicity] ?? null,
    };
}

export const TermsFormDisplay = (props: TermsFormDisplayProps) => {
    const { termForm, legalIssues = [], additionalCovenants = [], readOnly = false, onChange, errors } = props;
    const summary = termForm.purchasePriceSummary;
    const total = summaryTotal(summary);
    const loiDatePickerRef = useRef<any>(null);

    const update = useCallback(
        (patch: Partial<TermForm>) => {
            onChange?.({ ...termForm, ...patch });
        },
        [termForm, onChange]
    );

    const updateSummary = useCallback(
        (patch: Partial<PurchasePriceSummary>) => {
            onChange?.({
                ...termForm,
                purchasePriceSummary: {
                    ...(termForm.purchasePriceSummary ?? {}),
                    ...patch,
                },
            });
        },
        [termForm, onChange]
    );

    const setDfpRows = useCallback(
        (rows: DeferredFixedPaymentRow[]) => {
            updateSummary({ deferredFixedPayments: { rows } });
        },
        [updateSummary]
    );
    const setDvpRows = useCallback(
        (rows: DeferredVariablePaymentRow[]) => {
            updateSummary({ deferredVariablePayments: { rows } });
        },
        [updateSummary]
    );
    const setJvRow = useCallback(
        (row: JvEquityRow | null) => {
            updateSummary({ jvEquity: { row } });
        },
        [updateSummary]
    );
    const setCorpRow = useCallback(
        (row: CorporateEquityRow | null) => {
            updateSummary({ corporateEquity: { row } });
        },
        [updateSummary]
    );

    const dfpRows = summary?.deferredFixedPayments?.rows ?? [];
    const dvpRows = summary?.deferredVariablePayments?.rows ?? [];
    const hasDfpValue = dfpRows.length > 0 && totalDfp(dfpRows) > 0;
    const hasDvpValue = dvpRows.length > 0 && totalDvp(dvpRows) > 0;
    const jvRow = summary?.jvEquity?.row ?? null;
    const corpRow = summary?.corporateEquity?.row ?? null;
    const jvVal = summary?.jvEquity?.row?.valueAtClosing;
    const hasJvValue = jvVal != null && Number(jvVal) !== 0;
    const corpVal = summary?.corporateEquity?.row?.valueAtClosing;
    const hasCorpValue = corpVal != null && Number(corpVal) !== 0;

    const earnoutSum = dvpRows
        .filter((r) => r.type === DeferredVariablePaymentType.Earnout)
        .reduce((s, r) => s + (Number(r.valueAtClosing) || 0), 0);
    const syntheticJvSum = dvpRows
        .filter((r) => r.type === DeferredVariablePaymentType.Synthetic)
        .reduce((s, r) => s + (Number(r.valueAtClosing) || 0), 0);

    const dfpInvalidCells = useMemo(() => {
        if (!errors) return undefined;
        const cells = new Set<string>();
        Array.from(errors).forEach((key) => {
            if (key.startsWith('dfp:')) cells.add(key.substring(4));
        });
        return cells.size > 0 ? cells : undefined;
    }, [errors]);

    const dvpInvalidCells = useMemo(() => {
        if (!errors) return undefined;
        const cells = new Set<string>();
        Array.from(errors).forEach((key) => {
            if (key.startsWith('dvp:')) cells.add(key.substring(4));
        });
        return cells.size > 0 ? cells : undefined;
    }, [errors]);

    const jvInvalidCells = useMemo(() => {
        if (!errors) return undefined;
        const cells = new Set<string>();
        Array.from(errors).forEach((key) => {
            if (key.startsWith('jv:')) cells.add(key.substring(3));
        });
        return cells.size > 0 ? cells : undefined;
    }, [errors]);

    const corpInvalidCells = useMemo(() => {
        if (!errors) return undefined;
        const cells = new Set<string>();
        Array.from(errors).forEach((key) => {
            if (key.startsWith('corp:')) cells.add(key.substring(5));
        });
        return cells.size > 0 ? cells : undefined;
    }, [errors]);

    return (
        <div className="terms-form-display">
            <div className="terms-form-section">
                <div className="terms-form-section-header">LOI Terms Input Form</div>
                <div className="terms-form-section-body">
                    <div className="terms-form-detail-block terms-form-summary-layout terms-form-loi-exclusivity-block">
                        <div className="terms-form-form-content">
                            <div className="terms-form-summary-row">
                                <div className="terms-form-col-item">LOI Expiration Date</div>
                                <div className="terms-form-col-value">
                                    {readOnly ? (
                                        <Input
                                            type="text"
                                            value={
                                                termForm.loiExpirationDate != null
                                                    ? termForm.loiExpirationDate.split('T')[0]
                                                    : ''
                                            }
                                            disabled
                                            className="form-control loi-form-input"
                                            aria-label="LOI Expiration Date"
                                        />
                                    ) : (
                                        <div
                                            className="terms-form-date-picker-wrap"
                                            onClick={() =>
                                                loiDatePickerRef.current?.setOpen?.(true)
                                            }>
                                            <div className={`d-flex small-border${errors?.has('loiExpirationDate') ? ' is-invalid' : ''}`}>
                                                <InputGroupAddon
                                                    addonType="prepend"
                                                    onClick={() =>
                                                        loiDatePickerRef.current?.setOpen?.(true)
                                                    }>
                                                    <div className="input-group-text">
                                                        <FontAwesomeIcon icon={faCalendarAlt} />
                                                    </div>
                                                </InputGroupAddon>
                                                <DatePicker
                                                    ref={loiDatePickerRef}
                                                    selected={
                                                        termForm.loiExpirationDate
                                                            ? new Date(termForm.loiExpirationDate)
                                                            : null
                                                    }
                                                    onChange={(date: Date | null) =>
                                                        update({
                                                            loiExpirationDate: date
                                                                ? date.toISOString().slice(0, 10)
                                                                : null,
                                                        })
                                                    }
                                                    dateFormat="MM/dd/yyyy"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="terms-form-summary-row">
                                <div className="terms-form-col-item">Exclusivity Period</div>
                                <div className="terms-form-col-value">
                                    <div className="terms-form-col-value-with-unit">
                                        <Input
                                            type="number"
                                            value={termForm.exclusivityPeriodMonth ?? ''}
                                            disabled={readOnly}
                                            onChange={(e) =>
                                                update({
                                                    exclusivityPeriodMonth:
                                                        e.target.value === ''
                                                            ? null
                                                            : Number(e.target.value),
                                                })
                                            }
                                            invalid={errors?.has('exclusivityPeriodMonth')}
                                            className="form-control loi-form-input"
                                            aria-label="Exclusivity Period"
                                        />
                                        <span className="terms-form-unit-label">Months</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <TermsFormPropertyPurchasedBlock
                        termForm={termForm}
                        readOnly={readOnly}
                        onChange={onChange}
                        errors={errors}
                    />

                    <TermsFormPriceSummaryBlock
                        summary={summary}
                        total={total}
                        onSummaryChange={updateSummary}
                        earnoutSum={earnoutSum}
                        syntheticJvSum={syntheticJvSum}
                        readOnly={readOnly}
                        errors={errors}
                    />

                    <TermsFormGeneralDeferredPaymentsBlock
                        termForm={termForm}
                        readOnly={readOnly}
                        onChange={onChange}
                        errors={errors}
                    />

                    {hasDfpValue && (
                        <TermsFormDetailBlock
                            className="terms-form-detail-block--deferred-fixed-payments"
                            hideLastRowBorder
                            title="Deferred Fixed Payments"
                            columns={DEFERRED_FIXED_PAYMENT_COLUMNS}
                            rows={dfpRows.map(rowToKeyValue)}
                            readOnly={readOnly}
                            onRowsChange={(rowsKv) =>
                                setDfpRows(rowsKv.map((kv) => rowFromKeyValue(kv)))
                            }
                            displayTotal={totalDfp(dfpRows)}
                            showTotalRow
                            onAddRow={() =>
                                setDfpRows([
                                    ...dfpRows,
                                    {
                                        paymentTiming: null,
                                        amount: null,
                                        annualizedInterestAccrualRate: null,
                                        contingent: null,
                                        contingencyDependentVariableValue: null,
                                        contDvMinimumValue: null,
                                        employmentContingency: null,
                                        tailPeriodForEmployment: null,
                                    },
                                ])
                            }
                            onRemoveRow={(i) => setDfpRows(dfpRows.filter((_, idx) => idx !== i))}
                            expandableTextInputs
                            currencyColumns={DEFERRED_FIXED_PAYMENT_CURRENCY_COLUMNS}
                            percentageColumns={DEFERRED_FIXED_PAYMENT_PERCENTAGE_COLUMNS}
                            invalidCells={dfpInvalidCells}
                        />
                    )}

                    {hasDvpValue && (
                        <TermsFormDetailBlock
                            className="terms-form-detail-block--deferred-variable-payments"
                            hideLastRowBorder
                            title="Deferred Variable Payments"
                            columns={DEFERRED_VARIABLE_PAYMENT_COLUMNS}
                            rows={dvpRows.map(dvpRowToKeyValue)}
                            readOnly={readOnly}
                            onRowsChange={(rowsKv) =>
                                setDvpRows(rowsKv.map((kv) => dvpRowFromKeyValue(kv)))
                            }
                            onAddRow={() =>
                                setDvpRows([
                                    ...dvpRows,
                                    {
                                        paymentTiming: null,
                                        type: null,
                                        valueAtClosing: null,
                                        calculationDependentVariable: null,
                                        calcDvBaselineValue: null,
                                        calculationMultiple: null,
                                        maximumPayment: null,
                                        employmentContingency: null,
                                        tailPeriodForEmploymentContingency: null,
                                        penaltyMultiple: null,
                                        distributionPeriodicity: null,
                                    },
                                ])
                            }
                            onRemoveRow={(i) => setDvpRows(dvpRows.filter((_, idx) => idx !== i))}
                            selectOptions={{
                                [DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.type]: [
                                    DeferredVariablePaymentType.Earnout,
                                    DeferredVariablePaymentType.Synthetic,
                                ],
                            }}
                            showPaymentNumber
                            paymentNumberHeader=""
                            expandableTextInputs
                            currencyColumns={DEFERRED_VARIABLE_PAYMENT_CURRENCY_COLUMNS}
                            invalidCells={dvpInvalidCells}
                        />
                    )}

                    {hasJvValue && (
                        <TermsFormDetailBlock
                            className="terms-form-detail-block--jv-equity"
                            title="JV Equity"
                            columns={JV_EQUITY_COLUMNS}
                            rows={[
                                {
                                    [JV_EQUITY_COLUMN_LABELS.valueAtClosing]:
                                        jvRow?.valueAtClosing != null
                                            ? String(jvRow.valueAtClosing)
                                            : (jvVal != null ? String(jvVal) : null),
                                    [JV_EQUITY_COLUMN_LABELS.paymentTiming]:
                                        jvRow?.paymentTiming ?? null,
                                    [JV_EQUITY_COLUMN_LABELS.calculationDependentVariable]:
                                        jvRow?.calculationDependentVariable ?? null,
                                    [JV_EQUITY_COLUMN_LABELS.calcDvBaselineValue]:
                                        jvRow?.calcDvBaselineValue != null
                                            ? String(jvRow.calcDvBaselineValue)
                                            : null,
                                    [JV_EQUITY_COLUMN_LABELS.calculationMultiple]:
                                        jvRow?.calculationMultiple ?? null,
                                    [JV_EQUITY_COLUMN_LABELS.employmentContingency]:
                                        jvRow?.employmentContingency ?? null,
                                    [JV_EQUITY_COLUMN_LABELS.tailPeriodForEmploymentContingency]:
                                        jvRow?.tailPeriodForEmploymentContingency ?? null,
                                    [JV_EQUITY_COLUMN_LABELS.penaltyMultiple]:
                                        jvRow?.penaltyMultiple ?? null,
                                    [JV_EQUITY_COLUMN_LABELS.distributionPeriodicity]:
                                        jvRow?.distributionPeriodicity ?? null,
                                },
                            ]}
                            readOnly={readOnly}
                            onRowsChange={(rowsKv) =>
                                setJvRow(
                                    rowsKv.length > 0
                                        ? {
                                              valueAtClosing:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.valueAtClosing] != null &&
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.valueAtClosing] !== ''
                                                      ? Number(rowsKv[0][JV_EQUITY_COLUMN_LABELS.valueAtClosing])
                                                      : null,
                                              paymentTiming:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.paymentTiming] ?? null,
                                              calculationDependentVariable:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.calculationDependentVariable] ??
                                                  null,
                                              calcDvBaselineValue:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.calcDvBaselineValue] != null &&
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.calcDvBaselineValue] !== ''
                                                      ? Number(rowsKv[0][JV_EQUITY_COLUMN_LABELS.calcDvBaselineValue])
                                                      : null,
                                              calculationMultiple:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.calculationMultiple] ?? null,
                                              employmentContingency:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.employmentContingency] ?? null,
                                              tailPeriodForEmploymentContingency:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.tailPeriodForEmploymentContingency] ??
                                                  null,
                                              penaltyMultiple:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.penaltyMultiple] ?? null,
                                              distributionPeriodicity:
                                                  rowsKv[0][JV_EQUITY_COLUMN_LABELS.distributionPeriodicity] ?? null,
                                            }
                                        : null
                                )
                            }
                            singleRow
                            hideAddRow
                            showRowBorder={false}
                            expandableTextInputs
                            currencyColumns={JV_EQUITY_CURRENCY_COLUMNS}
                            invalidCells={jvInvalidCells}
                        />
                    )}

                    {hasCorpValue && (
                        <TermsFormDetailBlock
                            title="Corporate Equity"
                            columns={CORPORATE_EQUITY_COLUMNS}
                            rows={[
                                {
                                    [CORPORATE_EQUITY_COLUMN_LABELS.valueAtClosing]:
                                        corpRow?.valueAtClosing != null
                                            ? String(corpRow.valueAtClosing)
                                            : (corpVal != null ? String(corpVal) : null),
                                    [CORPORATE_EQUITY_COLUMN_LABELS.expectedTimingToRecap]:
                                        corpRow?.expectedTimingToRecap ?? null,
                                    [CORPORATE_EQUITY_COLUMN_LABELS.expectedPayoutAsMultipleOfCurrentValue]:
                                        corpRow?.expectedPayoutAsMultipleOfCurrentValue ?? null,
                                },
                            ]}
                            readOnly={readOnly}
                            onRowsChange={(rowsKv) =>
                                setCorpRow(
                                    rowsKv.length > 0
                                        ? {
                                              valueAtClosing:
                                                  rowsKv[0][CORPORATE_EQUITY_COLUMN_LABELS.valueAtClosing] != null &&
                                                  rowsKv[0][CORPORATE_EQUITY_COLUMN_LABELS.valueAtClosing] !== ''
                                                      ? Number(rowsKv[0][CORPORATE_EQUITY_COLUMN_LABELS.valueAtClosing])
                                                      : null,
                                              expectedTimingToRecap:
                                                  rowsKv[0][CORPORATE_EQUITY_COLUMN_LABELS.expectedTimingToRecap] ?? null,
                                              expectedPayoutAsMultipleOfCurrentValue:
                                                  rowsKv[0][CORPORATE_EQUITY_COLUMN_LABELS.expectedPayoutAsMultipleOfCurrentValue] ?? null,
                                          }
                                        : null
                                )
                            }
                            singleRow
                            showRowBorder={false}
                            currencyColumns={CORPORATE_EQUITY_CURRENCY_COLUMNS}
                            invalidCells={corpInvalidCells}
                        />
                    )}

                    <TermsFormLeaseTermsBlock
                        termForm={termForm}
                        readOnly={readOnly}
                        onChange={onChange}
                        errors={errors}
                    />

                    <TermsFormNonCompetitionBlock
                        legalIssues={legalIssues}
                        additionalCovenants={additionalCovenants}
                        readOnly={readOnly}
                    />
                </div>
            </div>
        </div>
    );
};
